import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaPlus, FaMinus, FaShoppingCart } from 'react-icons/fa';
import '../styles/Cart.css';

// Вспомогательная функция для округления до 1 знака после запятой
const roundToOne = (num) => Math.round(num * 10) / 10;

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getUnitLabel = (unit) => {
    switch (unit) {
      case 'kg': return t('kg');
      case 'piece': return t('piece');
      case 'box': return t('boxUnit');
      default: return unit;
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="empty-cart">
        <FaShoppingCart className="empty-cart-icon" />
        <h2>{t('cartEmpty')}</h2>
        <p>{t('addFromCatalog')}</p>
        <button onClick={() => navigate('/catalog')} className="btn-primary">
          {t('goToCatalog')}
        </button>
      </div>
    );
  }

  const total = getCartTotal();
  const MINIMUM_ORDER_AMOUNT = 150;
  const isMinimumReached = total >= MINIMUM_ORDER_AMOUNT;

  return (
    <div className="cart-page">
      <div className="cart-container">
        <div className="cart-header">
          <h1>{t('cart')}</h1>
          <button onClick={clearCart} className="btn-clear-cart">
            {t('clearCart')}
          </button>
        </div>

        <div className="cart-items">
          {cart.map((item) => {
            // Determine price based on unit
            let price;
            let originalPrice;
            let discount = 0;
            
            if (item.unit === 'box') {
              price = item.product.finalBoxPrice || item.product.boxPrice || item.product.price * (item.product.boxQuantity || 1);
              originalPrice = item.product.boxPrice;
              if (item.product.finalBoxPrice && item.product.finalBoxPrice < item.product.boxPrice) {
                discount = Math.round(((item.product.boxPrice - item.product.finalBoxPrice) / item.product.boxPrice) * 100);
              }
            } else {
              price = item.product.finalPrice || item.product.price;
              originalPrice = item.product.price;
              // Используем информацию о скидке
              const personalDiscount = item.product.personalDiscount || { type: null, value: 0 };
              const generalDiscount = item.product.generalDiscount || 0;
              
              if (personalDiscount.type === 'percent' && personalDiscount.value > 0) {
                discount = personalDiscount.value;
              } else if (personalDiscount.type === 'fixed' && personalDiscount.value > 0) {
                discount = Math.round((personalDiscount.value / originalPrice) * 100);
              } else if (personalDiscount.type === 'fixed_price' && personalDiscount.value > 0) {
                discount = Math.round(((originalPrice - personalDiscount.value) / originalPrice) * 100);
              } else if (generalDiscount > 0) {
                discount = generalDiscount;
              }
            }
            
            const itemTotal = price * item.quantity;
            const unitLabel = item.unit === 'box' ? t('boxUnit') : getUnitLabel(item.product.unit);

            return (
              <div key={`${item.product._id}-${item.unit}`} className="cart-item">
                <div className="cart-item-image">
                  <img 
                    src={item.product.imageUrl || '/images/placeholder.jpg'} 
                    alt={item.product.name}
                    onError={(e) => {
                      e.target.src = '/images/placeholder.jpg';
                    }}
                  />
                </div>

                <div className="cart-item-info">
                  <h3>
                    {item.product.name}
                    {item.unit === 'box' && (
                      <span className="unit-badge">{t('box')} ({item.product.boxQuantity} {t('pieces')})</span>
                    )}
                  </h3>
                  <div className="cart-item-price">
                    {discount > 0 && (
                      <>
                        <span className="original-price">
                          {originalPrice.toFixed(2)} €
                        </span>
                        <span className="discount-badge-small">-{discount}%</span>
                      </>
                    )}
                    <span className="current-price">
                      {price.toFixed(2)} € / {unitLabel}
                    </span>
                  </div>
                </div>

                <div className="cart-item-quantity">
                  {(() => {
                    // Кратность для кг товаров
                    const multiplicity = item.product.unit === 'kg' && item.unit !== 'box' 
                      ? (item.product.multiplicity || 1) 
                      : 1;
                    const isKgProduct = item.product.unit === 'kg' && item.unit !== 'box';
                    
                    return (
                      <>
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.product._id, Math.max(0, roundToOne(item.quantity - multiplicity)), item.unit)}
                        >
                          <FaMinus />
                        </button>
                        {isKgProduct ? (
                          // Для кг товаров - только отображение, без ручного ввода
                          // Фиксированная ширина для чисел до 99.9 кг
                          <span 
                            className="quantity-display-fixed"
                            style={{
                              width: '75px',
                              padding: '6px 8px',
                              textAlign: 'center',
                              fontWeight: '500',
                              display: 'inline-block',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              background: '#f9f9f9'
                            }}
                          >
                            {roundToOne(item.quantity)} {t('kg')}
                          </span>
                        ) : (
                          // Для штучных товаров - обычный input
                          <input
                            type="number"
                            value={roundToOne(item.quantity)}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || multiplicity;
                              const roundedVal = roundToOne(Math.round(val / multiplicity) * multiplicity);
                              updateQuantity(item.product._id, Math.max(multiplicity, roundedVal), item.unit);
                            }}
                            min={multiplicity}
                            step={multiplicity}
                            className="quantity-input"
                          />
                        )}
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.product._id, roundToOne(item.quantity + multiplicity), item.unit)}
                        >
                          <FaPlus />
                        </button>
                      </>
                    );
                  })()}
                </div>

                <div className="cart-item-total">
                  <span className="item-total-price">{itemTotal.toFixed(2)} €</span>
                </div>

                <button
                  className="cart-item-remove"
                  onClick={() => removeFromCart(item.product._id, item.unit)}
                  title={t('remove')}
                >
                  <FaTrash />
                </button>
              </div>
            );
          })}
        </div>

        <div className="cart-summary">
          <div className="summary-row">
            <span>{t('itemsInCart')}</span>
            <span>{cart.length}</span>
          </div>
          <div className="summary-row total-row">
            <span>{t('total')}</span>
            <span className="total-price">{total.toFixed(2)} €</span>
          </div>
          
          {!isMinimumReached && (
            <div className="minimum-order-warning">
              <p>{t('minimumOrderWarning', 'Минимальная сумма заказа: {{amount}}€', { amount: MINIMUM_ORDER_AMOUNT })}</p>
              
            </div>
          )}
          
          <div className="price-disclaimer">
          <small
            dangerouslySetInnerHTML={{
              __html: t(
                'priceDisclaimer',
                'Конечная цена может отличаться в зависимости от комплектации.<br/>К сумме добавляется НДС.'
              )
            }}
          />
          </div>
          
          <button 
            onClick={handleCheckout} 
            className="btn-checkout"
            disabled={!isMinimumReached}
            style={{ opacity: !isMinimumReached ? 0.6 : 1, cursor: !isMinimumReached ? 'not-allowed' : 'pointer' }}
          >
            {t('checkout')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cart;

